import { ChatConversation, ChatMessage, ChatSnapshot } from "../types";

function coerceString(value: unknown): string | undefined {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || undefined;
  }
  return undefined;
}

function ensureArray<T>(value: unknown, map: (item: unknown) => T | null | undefined): T[] {
  if (!Array.isArray(value)) return [];
  const result: T[] = [];
  for (const item of value) {
    const mapped = map(item);
    if (mapped != null) {
      result.push(mapped);
    }
  }
  return result;
}

export function normalizeMessage(raw: unknown): ChatMessage {
  if (!raw || typeof raw !== "object") {
    throw new Error("Invalid chat message payload");
  }

  const data = raw as Record<string, unknown>;
  const id = coerceString(data.id) ?? `local-${Date.now()}`;
  const conversationId = coerceString(data.conversationId) ?? coerceString(data.threadId) ?? "";
  if (!conversationId) {
    throw new Error("Conversation identifier missing from message payload");
  }

  return {
    id,
    conversationId,
    text: coerceString(data.text) ?? coerceString(data.body) ?? "",
    senderId: coerceString(data.senderId) ?? "",
    senderName: coerceString(data.senderName) ?? coerceString(data.sender) ?? null,
    sentAt: coerceString(data.sentAt) ?? coerceString(data.time) ?? null,
    time: coerceString(data.time) ?? coerceString(data.sentAt) ?? null,
    metadata: (data.metadata && typeof data.metadata === "object" ? (data.metadata as Record<string, unknown>) : undefined) ?? {},
  };
}

export function normalizeConversation(raw: unknown): ChatConversation {
  if (!raw || typeof raw !== "object") {
    throw new Error("Invalid conversation payload");
  }

  const data = raw as Record<string, unknown>;
  const id = coerceString(data.id);
  if (!id) {
    throw new Error("Conversation payload missing identifier");
  }

  const messages = ensureArray<ChatMessage>(data.messages, (item) => {
    try {
      return normalizeMessage(item);
    } catch {
      return null;
    }
  });

  const members = ensureArray<ChatConversation["members"][number]>(data.members, (item) => {
    if (!item || typeof item !== "object") return null;
    const member = item as Record<string, unknown>;
    const userId = coerceString(member.userId);
    if (!userId) return null;
    return {
      userId,
      displayName: coerceString(member.displayName) ?? null,
      role: coerceString(member.role) ?? null,
      muted: Boolean(member.muted),
      unreadCount: typeof member.unreadCount === "number" ? member.unreadCount : undefined,
      lastReadMessageId: coerceString(member.lastReadMessageId) ?? null,
      lastReadAt: coerceString(member.lastReadAt) ?? null,
      initials: coerceString(member.initials) ?? null,
    };
  });

  const membershipRaw = data.membership && typeof data.membership === "object" ? (data.membership as Record<string, unknown>) : null;
  const membershipUserId = membershipRaw ? coerceString(membershipRaw.userId) : undefined;
  const membership = membershipUserId ? members.find((member) => member.userId === membershipUserId) ?? null : null;

  return {
    id,
    name: coerceString(data.name) ?? null,
    title: coerceString(data.title) ?? null,
    status: coerceString(data.status) ?? null,
    group: Boolean(data.group ?? data.isGroup),
    unreadCount: typeof data.unreadCount === "number" ? data.unreadCount : 0,
    lastMessage: coerceString(data.lastMessage) ?? null,
    lastMessageId: coerceString(data.lastMessageId) ?? null,
    time: coerceString(data.time) ?? null,
    messages,
    members,
    membership,
    participantPresence:
      data.participantPresence && typeof data.participantPresence === "object"
        ? (data.participantPresence as Record<string, string>)
        : undefined,
    participants: ensureArray<string>(data.participants, (item) => coerceString(item) ?? "").filter(Boolean),
    messagesPage: typeof data.messagesPage === "number" ? data.messagesPage : undefined,
    messagesPageSize: typeof data.messagesPageSize === "number" ? data.messagesPageSize : undefined,
    messagesHasMore: typeof data.messagesHasMore === "boolean" ? data.messagesHasMore : undefined,
    messagesBefore: coerceString(data.messagesBefore) ?? null,
    messagesAfter: coerceString(data.messagesAfter) ?? null,
    metadata: data.metadata && typeof data.metadata === "object" ? { ...(data.metadata as Record<string, unknown>) } : null,
  };
}

export function cloneSnapshot(snapshot: ChatSnapshot): ChatSnapshot {
  return {
    roster: Object.fromEntries(
      Object.entries(snapshot.roster).map(([id, conversation]) => [id, { ...conversation, members: [...conversation.members], messages: [...conversation.messages] }])
    ),
    conversations: {
      inbox: snapshot.conversations.inbox.map((conversation) => snapshot.roster[conversation.id] ?? conversation),
      unread: snapshot.conversations.unread.map((conversation) => snapshot.roster[conversation.id] ?? conversation),
      communities: snapshot.conversations.communities.map((conversation) => snapshot.roster[conversation.id] ?? conversation),
    },
  };
}

